import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, ErrorNotice, Troubleshooting } from '../components/ui';

interface LivenessResult {
  deviceId: string;
  live: boolean;
  registerable: boolean;
  status: string;
  troubleshooting?: { reason: string; steps: string[] };
}

/**
 * Wireframe B4 — check the device, then register it.
 *
 * The two steps are deliberately separate (FR-B5): showing the registration
 * form for a device that cannot come online would waste an admin's time
 * filling in billing details for something that will never activate.
 */
export function RegisterDevice() {
  const navigate = useNavigate();
  const [deviceId, setDeviceId] = useState('');
  const [liveness, setLiveness] = useState<LivenessResult | null>(null);
  const [locationName, setLocationName] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [city, setCity] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [taxRate, setTaxRate] = useState('5');
  const [combine, setCombine] = useState(false);
  const [combinedBillingGroupId, setCombinedBillingGroupId] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function check(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setLiveness(null);
    try {
      setLiveness(await api.post<LivenessResult>('/devices/check', { deviceId }));
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function register(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ deviceId: string }>('/devices', {
        deviceId: liveness!.deviceId,
        locationName,
        locationAddress: { line1: addressLine || undefined, city: city || undefined },
        billingDetails: vatNumber ? { vatNumber } : undefined,
        taxDetails: { rate: Number(taxRate) },
        combinedBillingGroupId: combine && combinedBillingGroupId ? combinedBillingGroupId : undefined,
      });
      navigate(`/devices/${result.deviceId}`, { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Register a device</h1>
        <p>Enter the device ID printed on the appliance.</p>
      </div>

      <ErrorNotice error={error} />

      <Card title="Device ID">
        <form className="card-body" onSubmit={check}>
          <div className="field">
            <label htmlFor="deviceId">Device ID</label>
            <input
              id="deviceId"
              className="mono"
              placeholder="NG-XXXX-XXXX"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              autoCapitalize="characters"
              autoCorrect="off"
              required
            />
            <div className="hint">Case and hyphens do not matter.</div>
          </div>
          <button type="submit" disabled={busy || !deviceId}>
            {busy ? 'Checking…' : 'Check device'}
          </button>
        </form>
      </Card>

      {liveness && !liveness.registerable && liveness.troubleshooting ? (
        <Troubleshooting reason={liveness.troubleshooting.reason} steps={liveness.troubleshooting.steps} />
      ) : null}

      {liveness?.registerable ? (
        <>
          <div className="notice ok">Device {liveness.deviceId} is live and ready to register.</div>
          <Card title="Location and billing">
            <form className="card-body" onSubmit={register}>
              <div className="field">
                <label htmlFor="location">Location name</label>
                <input id="location" value={locationName} onChange={(e) => setLocationName(e.target.value)} required />
              </div>
              <div className="row">
                <div className="field">
                  <label htmlFor="address">Address</label>
                  <input id="address" value={addressLine} onChange={(e) => setAddressLine(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="city">City</label>
                  <input id="city" value={city} onChange={(e) => setCity(e.target.value)} />
                </div>
              </div>
              <div className="row">
                <div className="field">
                  <label htmlFor="vat">Tax registration number</label>
                  <input id="vat" value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="rate">Tax rate (%)</label>
                  <input id="rate" type="number" min="0" max="100" step="0.5" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
                </div>
              </div>
              <div className="field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto', minHeight: 'auto' }}
                    checked={combine}
                    onChange={(e) => setCombine(e.target.checked)}
                  />
                  Combine billing with an existing business group
                </label>
                {combine ? (
                  <input
                    style={{ marginTop: 8 }}
                    placeholder="Business group ID"
                    value={combinedBillingGroupId}
                    onChange={(e) => setCombinedBillingGroupId(e.target.value)}
                  />
                ) : null}
              </div>
              <button type="submit" disabled={busy || !locationName}>
                {busy ? 'Registering…' : 'Complete registration'}
              </button>
            </form>
          </Card>
        </>
      ) : null}
    </>
  );
}
